import { Component, OnInit } from '@angular/core';
import { ThemeCustomizerService } from '../../common/theme-customizer/theme-customizer.service';

interface HeroStat {
  label: string;
  value: string;
}

interface CellPoint {
  title: string;
  description: string;
}

interface CommitteeMember {
  sno: number;
  name: string;
  designation: string;
  position: string;
}

interface PhotoNoteItem {
  image: string;
  caption: string;
}

@Component({
  selector: 'app-courses-grid-style-three-columns-four-page',
  templateUrl: './courses-grid-style-three-columns-four-page.component.html',
  styleUrls: ['./courses-grid-style-three-columns-four-page.component.scss']
})
export class CoursesGridStyleThreeColumnsFourPageComponent implements OnInit {
  isToggled = false;

  pageTitle = 'Research & Development Cell';
  pageSubtitle =
    'Promoting research culture, innovation, funded projects, collaboration, and real-world problem-solving among faculty and students.';

  admissionNotice =
    'Admissions open for 2026 : B.E (CIVIL), B.E (CSE), B.E (CSE - AI & ML), B.E (CSE - CS), B.E (ECE), B.E (EEE), B.E (MECH), MBA, M.E (CSE), M.E (Embedded System Technologies), M.E (Thermal Engineering).';

  heroStats: HeroStat[] = [
    { label: 'Focus', value: 'Research Culture' },
    { label: 'Growth', value: 'Innovation & Patents' },
    { label: 'Support', value: 'Projects & Grants' },
    { label: 'Outcome', value: 'Industry Collaboration' }
  ];

  rdIntro: string[] = [
    'The Research and Development (R&D) Cell at Fatima Michael College of Engineering and Technology plays a vital role in promoting research culture among faculty and students. The cell encourages innovative thinking, problem-solving, and technical advancements through continuous research activities across all departments.',
    'The main aim of the R&D Cell is to support and guide faculty and students in undertaking quality research work that contributes to the development of science, engineering, and technology. It motivates faculty members to publish their research work in reputed journals and present papers at national and international conferences. The cell also assists in applying for research grants from government agencies like AICTE, DST, and DRDO.',
    'To create awareness about current research trends and opportunities, the R&D Cell organizes seminars, workshops, guest lectures, and faculty development programs regularly. It also promotes student involvement in project development, participation in contests like Smart India Hackathon, and encourages filing of patents and launching startups.',
    'The cell works closely with industries and other academic institutions to carry out collaborative research. Through its dedicated efforts, the R&D Cell contributes to the overall academic growth of the college and prepares students to face real-world technological challenges with confidence and creativity.',
    'We follow the guidelines and functionalities proposed by UGC guidelines.'
  ];

  rdPoints: CellPoint[] = [
    {
      title: 'Promoting Research Culture',
      description:
        'The R&D Cell promotes a strong research environment among faculty and students through continuous academic and technical engagement.'
    },
    {
      title: 'Research Publications',
      description:
        'Faculty members are encouraged to publish papers in reputed journals and present their work in national and international conferences.'
    },
    {
      title: 'Research Grants Support',
      description:
        'The cell assists faculty and students in applying for funded research projects from agencies such as AICTE, DST, and DRDO.'
    },
    {
      title: 'Academic Events',
      description:
        'It regularly organizes seminars, workshops, guest lectures, and faculty development programmes to create awareness about emerging research trends and opportunities.'
    },
    {
      title: 'Student Innovation',
      description:
        'The cell promotes student participation in project development, innovation contests such as Smart India Hackathon, and practical problem-solving activities.'
    },
    {
      title: 'Patents and Startups',
      description:
        'It encourages the filing of patents, product development, and startup initiatives to transform ideas into impactful solutions.'
    },
    {
      title: 'Collaborative Research',
      description:
        'The R&D Cell works with industries and academic institutions to carry out collaborative research and knowledge-sharing initiatives.'
    },
    {
      title: 'Real-World Readiness',
      description:
        'Through research exposure and innovation activities, the cell prepares students to face real-world technological challenges with confidence and creativity.'
    }
  ];

  researchAdvisoryCouncil: CommitteeMember[] = [
    { sno: 1, name: 'Dr. P. Nelson Raja', designation: 'Principal', position: 'Chairperson' },
    { sno: 2, name: 'Dr. P. Ravikumaran', designation: 'Professor - CSE', position: 'Member' },
    { sno: 3, name: 'Dr. R. Natarajan', designation: 'Professor - EEE', position: 'Member' }
  ];

  rdCoordinator = {
    name: 'Dr. K. R. Rameela',
    designation: 'Professor',
    department: 'Department of Electrical and Electronics Engineering'
  };

  financeInfrastructureCommittee: CommitteeMember[] = [
    { sno: 1, name: 'Dr. G. Dhinesh', designation: 'MBA - Director', position: 'Coordinator' },
    { sno: 2, name: 'Mr. G. Balakrishnan', designation: 'Associate Professor - CSE', position: 'Member' },
    { sno: 3, name: 'Mr. J. Thavaseelan', designation: 'Assistant Professor - Civil', position: 'Member' }
  ];

  researchPolicyCommittee: CommitteeMember[] = [
    { sno: 1, name: 'Dr. S. Ramalingam', designation: 'Professor - S&H', position: 'Coordinator' },
    { sno: 2, name: 'Dr. S. Vijay Mallikraj', designation: 'Associate Professor - MBA', position: 'Member' },
    { sno: 3, name: 'Mr. M. Elayaraja', designation: 'Associate Professor - ECE', position: 'Member' }
  ];

  collaborationCommittee: CommitteeMember[] = [
    { sno: 1, name: 'Mr. M. Prabakaran', designation: 'Associate Professor - Mech', position: 'Coordinator' },
    { sno: 2, name: 'Mr. G. Alagumurugan', designation: 'Assistant Professor - Civil', position: 'Member' },
    { sno: 3, name: 'Ms. R. Nagadeepa', designation: 'Associate Professor - ECE', position: 'Member' }
  ];

  productDevelopmentCommittee: CommitteeMember[] = [
    { sno: 1, name: 'Mr. M. Kathiravan', designation: 'Associate Professor - Mech', position: 'Coordinator' },
    { sno: 2, name: 'Mr. H. Ganesh', designation: 'Assistant Professor - Mech', position: 'Member' },
    { sno: 3, name: 'Mr. B. M. Gnanasekaran', designation: 'Associate Professor - Automobile', position: 'Member' }
  ];

  iprCommittee: CommitteeMember[] = [
    { sno: 1, name: 'Mr. A. Athimoolam', designation: 'Associate Professor - S&H', position: 'Coordinator' },
    { sno: 2, name: 'Mr. M. Selvakumar', designation: 'Assistant Professor - Mech', position: 'Member' },
    { sno: 3, name: 'Dr. S. Muthupoongodi', designation: 'Associate Professor - S&H', position: 'Member' }
  ];

  rdEventPhotos: PhotoNoteItem[] = [
    {
      image: 'assets/img/rd/i1.jpeg',
      caption:
        "R & D Cell conducted Ideathon ’26 on 05.02.2026. It is an innovation challenge that encourages students from all years to showcase creativity and problem-solving skills."
    },
    {
      image: 'assets/img/rd/i2.jpeg',
      caption:
        "Ideathon ’26 invited original and impactful ideas across various domains to address real-world challenges and promote innovation-oriented thinking among students."
    }
  ];

  constructor(public themeService: ThemeCustomizerService) {
    this.themeService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  ngOnInit(): void {}
}